import store from "../../store";
import { loginResponse } from "../../store/Auth/actions";
import { showHideLoader } from "../../store/Loader/actions";
import { clearAdminData, getAuthBearerToken, getSessionPortal } from "../../utils/Auth";
import history from "../../routes/History";
import messages from "../../assets/locale/messages";
import { showErrorMsg } from "../../utils/Helpers";
import { resolvePortalLoginPath } from "../../utils/authPortal";
import {
  clearFirebaseToken,
  generateFirebaseToken,
} from "../../firebase/helpers";

export const isHandlerEnabled = (config = {}) => {
  return Object.prototype.hasOwnProperty.call(config, "handlerEnabled") &&
    !config.handlerEnabled
    ? false
    : true;
};

/**
 * Company OTP endpoints may return 401 for invalid/expired code; the signup UI must
 * show the API message and stay on the page — not global logout + redirect to login.
 */
const isCompanyOtpVerificationRequest = (config) => {
  if (!config) return false;
  const url = String(config.url ?? "");
  const baseURL = String(config.baseURL ?? "");
  const haystack = `${baseURL} ${url}`;
  return (
    /company_users\/verify_email/i.test(haystack) ||
    /company_users\/resend_email_verification/i.test(haystack)
  );
};

/**
 * Login endpoint (POST /sessions) returns 401 for invalid credentials.
 * These must NOT be captured by the forced-logout path — the saga owns
 * the error message for this case. Applies across all portal axios instances.
 */
const isLoginRequest = (config) => {
  if (!config) return false;
  return (
    String(config.method ?? "").toLowerCase() === "post" &&
    /^\/sessions$/i.test(String(config.url ?? "").trim())
  );
};

/** Wrong current password may be 401; user should stay on the form and see the API message, not global logout. */
const isCompanyChangePasswordRequest = (config) => {
  if (!config) return false;
  const url = String(config.url ?? "");
  const baseURL = String(config.baseURL ?? "");
  const haystack = `${baseURL} ${url}`;
  return /company_users\/change_password/i.test(haystack);
};

const isClientChangePasswordRequest = (config) => {
  if (!config) return false;
  const url = String(config.url ?? "");
  const baseURL = String(config.baseURL ?? "");
  const haystack = `${baseURL} ${url}`;
  return /clients\/change_password/i.test(haystack);
};

/**
 * Backend flags a deactivated/invalidated session (e.g. company employee whose
 * role was deactivated) via this message, sometimes without a 401 status —
 * so it must be checked independently of `status` to force logout.
 */
const isUnauthenticatedSessionMessage = (error) => {
  const message = error?.response?.data?.message;
  return typeof message === "string" && /unauthenticated/i.test(message);
};

// This is used to handle remove loader only if all pending requests are resolved
let numberOfAjaxCAllPending = 0;

export const requestHandler = (request) => {
  numberOfAjaxCAllPending++;
  if (isHandlerEnabled(request)) {
    store.dispatch(showHideLoader(true));
  }

  // Locale + auth must run for every request (e.g. OTP uses handlerEnabled: false).
  const locale =
    store.getState().locale.lang || localStorage.getItem("lang") || "en";
  if (!request.headers) {
    request.headers = {};
  }
  request.headers["locale"] = locale;
  const requestParams = { ...(request.params || {}) };
  request.params = {
    locale,
    ...requestParams,
  };

  const token = getAuthBearerToken();
  if (token) {
    request.headers["Authorization"] = `Bearer ${token}`;
  }

  return request;
};

export const successHandler = (response) => {
  numberOfAjaxCAllPending--;
  if (isHandlerEnabled(response)) {
    if (numberOfAjaxCAllPending === 0) {
      store.dispatch(showHideLoader(false));
    }
  }
  return response;
};

export const errorHandler = (error) => {
  numberOfAjaxCAllPending--;
  if (isHandlerEnabled(error.config)) {
    if (numberOfAjaxCAllPending === 0) {
      store.dispatch(showHideLoader(false));
    }
  }
  const lang = store.getState().locale.lang;
  const failedConfig = error?.config ?? error?.response?.config;
  // Login-endpoint failures (wrong credentials) must propagate to the saga so
  // it can display the specific API error message. The forced-logout path must
  // not intercept them — there is no session to clear on a failed login attempt.
  if (isLoginRequest(failedConfig)) {
    return Promise.reject(error);
  }
  if (
    (error?.response?.status === 401 ||
      isUnauthenticatedSessionMessage(error)) &&
    !isCompanyOtpVerificationRequest(failedConfig) &&
    !isCompanyChangePasswordRequest(failedConfig) &&
    !isClientChangePasswordRequest(failedConfig)
  ) {
    const loginPath = resolvePortalLoginPath({
      sessionPortal: getSessionPortal(),
      pathname: history.location?.pathname,
    });
    store.dispatch(loginResponse({}));
    clearAdminData();
    clearFirebaseToken();
    generateFirebaseToken();
    history.push(loginPath);
    // Forced logout must be silent: the request's own caller (saga/hook) would
    // otherwise catch this rejection and surface the raw API message (e.g.
    // "Invalid credentials") as an error toast right as we redirect to login.
    // Never settling here stops that downstream .catch from ever running.
    return new Promise(() => {});
  } else if (isHandlerEnabled(error.config)) {
    if (error?.message === "Network Error") {
      showErrorMsg(messages[lang].shared.noInternetConnection);
    } else {
      showErrorMsg(messages[lang].shared.generalErrMsg);
    }
  }
  return Promise.reject(error);
};
